import { type LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: { value: number; isPositive: boolean };
  subtitle?: string;
  color?: 'default' | 'success' | 'warning' | 'critical';
}

const variantStyles = {
  default: {
    icon: 'text-slate-600',
    bg: 'bg-slate-100',
  },
  success: {
    icon: 'text-green-600',
    bg: 'bg-green-50',
  },
  warning: {
    icon: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  critical: {
    icon: 'text-red-600',
    bg: 'bg-red-50',
  },
};

export function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  subtitle,
  color = 'default',
}: MetricCardProps) {
  const styles = variantStyles[color];

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', styles.bg)}>
            <Icon className={cn('h-5 w-5', styles.icon)} />
          </div>
        </div>

        <div className="mt-3 flex items-baseline gap-2">
          <p className="text-3xl font-bold tracking-tight">{value}</p>
          {trend && (
            <span
              className={cn(
                'flex items-center text-sm font-medium',
                trend.isPositive ? 'text-green-600' : 'text-red-600'
              )}
            >
              {trend.isPositive ? (
                <TrendingUp className="h-4 w-4 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 mr-1" />
              )}
              {trend.value}%
            </span>
          )}
        </div>

        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
